import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import SEOHead from '../components/SEOHead';
import BrandLoadingScreen from '../components/BrandLoadingScreen';
import { calculateFirstYearPlan } from '../utils/literentMath';

const API_URL = process.env.REACT_APP_API_URL;

// ─── Paystack direct-debit supported banks (static fallback) ──────────────────
// Source: Paystack docs — banks that support direct debit via NIBSS mandate.
// The app fetches the live list from Paystack on mount and uses this only if
// the fetch fails. Add more as Paystack onboards new banks.
const DIRECT_DEBIT_BANKS = [
  { name: 'Access Bank',              code: '044' },
  { name: 'Citibank Nigeria',         code: '023' },
  { name: 'Ecobank Nigeria',          code: '050' },
  { name: 'Fidelity Bank',            code: '070' },
  { name: 'First Bank of Nigeria',    code: '011' },
  { name: 'First City Monument Bank', code: '214' },
  { name: 'Globus Bank',              code: '103' },
  { name: 'Guaranty Trust Bank',      code: '058' },
  { name: 'Heritage Bank',            code: '030' },
  { name: 'Jaiz Bank',                code: '301' },
  { name: 'Keystone Bank',            code: '082' },
  { name: 'Polaris Bank',             code: '076' },
  { name: 'Providus Bank',            code: '101' },
  { name: 'Stanbic IBTC Bank',        code: '221' },
  { name: 'Standard Chartered Bank',  code: '068' },
  { name: 'Sterling Bank',            code: '232' },
  { name: 'Union Bank of Nigeria',    code: '032' },
  { name: 'United Bank for Africa',   code: '033' },
  { name: 'Unity Bank',               code: '215' },
  { name: 'Wema Bank',                code: '035' },
  { name: 'Zenith Bank',              code: '057' },
];

function formatNaira(n) {
  return `₦${Number(n || 0).toLocaleString('en-NG')}`;
}

// ─── Individual Question Slide ─────────────────────────────────────────────
function Slide({ visible, children }) {
  return (
    <div
      className={`transition-all duration-500 ease-in-out ${
        visible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-6 pointer-events-none absolute inset-0'
      }`}
    >
      {children}
    </div>
  );
}

// ─── Progress Bar ──────────────────────────────────────────────────────────
function ProgressBar({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="w-full bg-[#e8f2d1] rounded-full h-1.5 mb-8">
      <div
        className="h-1.5 rounded-full bg-[#294c79] transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ─── Shared input styles ───────────────────────────────────────────────────
const inputCls =
  'w-full px-5 py-4 text-lg rounded-2xl border-2 border-[#d9e5ef] bg-white focus:outline-none focus:border-[#294c79] focus:ring-4 focus:ring-[#294c79]/10 transition placeholder-[#1a1a1a]/30';
const labelCls = 'block text-2xl font-bold text-[#1a1a2e] mb-1';
const hintCls = 'text-sm text-[#1a1a1a]/50 mb-5';
const btnCls =
  'mt-6 px-8 py-3.5 rounded-2xl bg-[#294c79] text-white font-semibold text-base hover:bg-[#1f3a5c] active:scale-95 transition-all shadow-md disabled:opacity-40 disabled:cursor-not-allowed';
const backBtnCls =
  'mt-3 px-6 py-3 rounded-2xl border border-[#d9e5ef] text-[#294c79] font-medium text-sm hover:bg-[#f3f7fb] transition';

export default function ApplyPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const house = location.state?.house;

  const [slide, setSlide] = useState(0);
  const [profileComplete, setProfileComplete] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  // Starts with the static fallback; replaced by live Paystack list on mount
  const [banks, setBanks] = useState(DIRECT_DEBIT_BANKS);
  const [banksLoading, setBanksLoading] = useState(true);
  const [resolvingAccount, setResolvingAccount] = useState(false);
  const [accountNameError, setAccountNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // guarantorFormLoading removed — form download moved to verification stage in dashboard
  const inputRef = useRef(null);

  const [fd, setFd] = useState({
    occupation: '',
    employer: '',
    monthlyIncome: '',
    yearsEmployed: '',
    bankName: '',
    bankCode: '',
    accountNumber: '',
    accountName: '',
    bvn: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    guarantor1Name: '',
    guarantor1Phone: '',
    guarantor1Relationship: '',
    guarantor1Address: '',
    guarantor1Occupation: '',
    guarantor1Employer: '',
    guarantor2Name: '',
    guarantor2Phone: '',
    guarantor2Relationship: '',
    guarantor2Address: '',
    guarantor2Occupation: '',
    guarantor2Employer: '',
    termsAccepted: false,
  });

  const plan = useMemo(
    () => calculateFirstYearPlan(house?.annual_rent ?? house?.annualRent ?? 0),
    [house]
  );

  // ── Prefill from profile ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('authToken');
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const p = res.data.profile || {};
        setProfileComplete(p.profile_complete === true);
        const applicantType = p.applicant_type || 'employed';
        const debitBankName = applicantType === 'student' ? p.parent_bank_name : applicantType === 'nysc' ? p.nysc_allawee_bank_name : p.bank_name;
        const debitAccountNumber = applicantType === 'student' ? p.parent_account_number : applicantType === 'nysc' ? p.nysc_allawee_account_number : p.account_number;
        const debitAccountName = applicantType === 'student' ? p.parent_bank_account_name : applicantType === 'nysc' ? p.nysc_allawee_account_name : p.bank_account_name;
        setFd(prev => ({
          ...prev,
          occupation: prev.occupation || p.job_title || p.parent_occupation || p.nysc_ppa_type || '',
          employer: prev.employer || p.employer_name || p.parent_employer || p.nysc_ppa_name || '',
          monthlyIncome: prev.monthlyIncome || p.monthly_net_income || '',
          bankName: prev.bankName || debitBankName || '',
          accountNumber: prev.accountNumber || debitAccountNumber || '',
          accountName: prev.accountName || debitAccountName || '',
          bvn: prev.bvn || p.bvn || '',
          emergencyContactName: prev.emergencyContactName || p.emergency_contact_name || '',
          emergencyContactPhone: prev.emergencyContactPhone || p.emergency_contact_phone || '',
          emergencyContactRelationship: prev.emergencyContactRelationship || p.emergency_contact_relationship || '',
          guarantor1Name: prev.guarantor1Name || p.guarantor1_full_name || '',
          guarantor1Phone: prev.guarantor1Phone || p.guarantor1_phone || '',
          guarantor1Relationship: prev.guarantor1Relationship || p.guarantor1_relationship || '',
          guarantor1Address: prev.guarantor1Address || p.guarantor1_address || '',
          guarantor1Occupation: prev.guarantor1Occupation || p.guarantor1_occupation || '',
          guarantor1Employer: prev.guarantor1Employer || p.guarantor1_employer || '',
          guarantor2Name: prev.guarantor2Name || p.guarantor2_full_name || '',
          guarantor2Phone: prev.guarantor2Phone || p.guarantor2_phone || '',
          guarantor2Relationship: prev.guarantor2Relationship || p.guarantor2_relationship || '',
          guarantor2Address: prev.guarantor2Address || p.guarantor2_address || '',
          guarantor2Occupation: prev.guarantor2Occupation || p.guarantor2_occupation || '',
          guarantor2Employer: prev.guarantor2Employer || p.guarantor2_employer || '',
        }));
      } catch {
        setProfileComplete(false);
      } finally {
        setLoadingProfile(false);
      }
    })();
  }, [user]);

  // ── Load live Paystack direct-debit banks ─────────────────────────────────
  // We call our own backend which hits Paystack's List Banks API with
  // ?pay_with_bank=true — this returns exactly the banks that support
  // direct debit / Pay with Bank (the same set used for mandates).
  // If that fails, the static DIRECT_DEBIT_BANKS fallback stays in place.
  useEffect(() => {
    setBanksLoading(true);
    axios
      .get(`${API_URL}/api/banks/direct-debit`)
      .then(res => {
        const list = res.data?.banks;
        // Only replace the static list if the API returns MORE or EQUAL banks.
        // If it returns fewer (e.g. Kuda+PalmPay only), keep the static list.
        if (Array.isArray(list) && list.length >= DIRECT_DEBIT_BANKS.length) {
          const normalised = list
            .filter(b => b.name && b.code)
            .map(b => ({ name: b.name, code: b.code }))
            .sort((a, b) => a.name.localeCompare(b.name));
          setBanks(normalised);
        }
      })
      .catch(() => {
        // Keep static fallback — no action needed
      })
      .finally(() => setBanksLoading(false));
  }, []);

  // ── Sync bank code when bank name changes ─────────────────────────────────
  useEffect(() => {
    if (fd.bankName && !fd.bankCode) {
      const found = banks.find(b => b.name === fd.bankName);
      if (found) setFd(prev => ({ ...prev, bankCode: found.code }));
    }
  }, [banks, fd.bankName, fd.bankCode]);

  // ── Auto-resolve account name ─────────────────────────────────────────────
  useEffect(() => {
    if (fd.bankCode && fd.accountNumber.length === 10) {
      setResolvingAccount(true);
      setAccountNameError('');
      axios
        .post(`${API_URL}/api/banks/resolve-account`, {
          bankCode: fd.bankCode,
          accountNumber: fd.accountNumber,
        })
        .then(res =>
          setFd(prev => ({ ...prev, accountName: res.data.account_name || '' }))
        )
        .catch(err =>
          setAccountNameError(err.response?.data?.error || 'Could not verify account.')
        )
        .finally(() => setResolvingAccount(false));
    }
  }, [fd.bankCode, fd.accountNumber]);

  // ── Focus input on slide change ───────────────────────────────────────────
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus?.(), 520);
  }, [slide]);

  const set = (field, value) => setFd(prev => ({ ...prev, [field]: value }));

  const setBank = name => {
    const found = banks.find(b => b.name === name);
    setFd(prev => ({
      ...prev,
      bankName: name,
      bankCode: found?.code || '',
      accountName: '',
    }));
    setAccountNameError('');
  };

  const setAcct = val => {
    setFd(prev => ({
      ...prev,
      accountNumber: val.replace(/\D/g, '').slice(0, 10),
      accountName: '',
    }));
    setAccountNameError('');
  };

  const next = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSlide(s => s + 1);
  };
  const back = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSlide(s => s - 1);
  };

  // Guarantor form download moved to verification stage in TenantDashboard

  const submitApplication = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${API_URL}/api/applications`,
        {
          houseId: house.id || house._id,
          personalInfo: {
            occupation: fd.occupation,
            employer: fd.employer,
            monthlyIncome: fd.monthlyIncome,
            yearsEmployed: fd.yearsEmployed,
          },
          financialInfo: {
            bankName: fd.bankName,
            bankCode: fd.bankCode,
            accountNumber: fd.accountNumber,
            accountName: fd.accountName,
            bvn: fd.bvn,
          },
          emergencyContact: {
            name: fd.emergencyContactName,
            phone: fd.emergencyContactPhone,
            relationship: fd.emergencyContactRelationship,
          },
          guarantor1: {
            name: fd.guarantor1Name,
            phone: fd.guarantor1Phone,
            relationship: fd.guarantor1Relationship,
            address: fd.guarantor1Address,
            occupation: fd.guarantor1Occupation,
            employer: fd.guarantor1Employer,
          },
          guarantor2: {
            name: fd.guarantor2Name,
            phone: fd.guarantor2Phone,
            relationship: fd.guarantor2Relationship,
            address: fd.guarantor2Address,
            occupation: fd.guarantor2Occupation,
            employer: fd.guarantor2Employer,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      next(); // go to success slide
    } catch (err) {
      setSubmitError(
        err.response?.data?.error ||
          'Could not submit your application right now. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Guards ───────────────────────────────────────────────────────────────
  if (!house)
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#f8fbff]">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-6">🏠</div>
          <h1 className="text-3xl font-bold text-[#294c79] mb-4">No House Selected</h1>
          <p className="text-[#1a1a1a]/60 mb-8">
            Please open a house from the browse page first.
          </p>
          <Link
            to="/browse"
            className="inline-block px-8 py-4 rounded-2xl bg-[#294c79] text-white font-semibold hover:bg-[#1f3a5c] transition"
          >
            Browse Homes
          </Link>
        </div>
      </div>
    );

  if (!user)
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#f8fbff]">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-6">🔐</div>
          <h1 className="text-3xl font-bold text-[#294c79] mb-4">Sign in to apply</h1>
          <Link
            to="/login"
            className="inline-block px-8 py-4 rounded-2xl bg-[#294c79] text-white font-semibold hover:bg-[#1f3a5c] transition"
          >
            Sign In
          </Link>
        </div>
      </div>
    );

  if (loadingProfile) return <BrandLoadingScreen message="Getting your application ready…" />;

  if (profileComplete === false)
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#f8fbff]">
        <div className="max-w-xl w-full bg-white rounded-3xl shadow-xl p-10 text-center border border-[#e8f2d1]">
          <div className="text-5xl mb-5">👤</div>
          <h1 className="text-2xl font-bold text-[#294c79] mb-3">
            Complete your profile first
          </h1>
          <p className="text-[#1a1a1a]/60 mb-8">
            LiteRent needs your full profile and identity documents before you can apply.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-8 py-4 rounded-2xl bg-[#294c79] text-white font-semibold hover:bg-[#1f3a5c] transition"
            >
              Go to Dashboard
            </button>
            <Link
              to={`/house/${house.id || house._id}`}
              className="px-8 py-4 rounded-2xl border border-[#294c79] text-[#294c79] font-semibold hover:bg-[#f3f7fb] transition"
            >
              Back to House
            </Link>
          </div>
        </div>
      </div>
    );

  const totalSteps = 14;

  return (
    <>
      <SEOHead
        title={`Apply for ${house.title || 'This Home'} | LiteRent`}
        description={`Apply for ${house.title || 'this home'} on LiteRent — fast, stage-based, and transparent.`}
        canonical="https://literent.com.ng/apply"
      />

      <div className="min-h-screen bg-gradient-to-br from-[#f8fbff] via-white to-[#f5f9ec] flex flex-col">
        {/* Top bar */}
        <header className="flex items-center justify-between px-5 sm:px-10 py-4 border-b border-[#edf2f7] bg-white/80 backdrop-blur-sm sticky top-0 z-10">
          <Link
            to={`/house/${house.id || house._id}`}
            className="flex items-center gap-2 text-[#294c79] font-semibold text-sm hover:opacity-75 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </Link>
          <div className="text-sm font-medium text-[#294c79]/60">
            {slide > 0 && slide <= totalSteps
              ? `${slide} of ${totalSteps}`
              : slide === 0
              ? "Let's begin"
              : ''}
          </div>
          <img src="/Logo.jpg" alt="LiteRent" className="h-8 w-auto rounded-lg" />
        </header>

        <main className="flex-1 flex items-center justify-center px-5 py-10 sm:px-8">
          <div className="w-full max-w-2xl relative">
            {/* Progress */}
            {slide > 0 && slide <= totalSteps && (
              <ProgressBar current={slide} total={totalSteps} />
            )}

            {/* ── SLIDE 0: Welcome / Property Summary ── */}
            <Slide visible={slide === 0}>
              <div className="text-center mb-8">
                <span className="inline-block bg-[#e8f2d1] text-[#4a7a1e] text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide mb-5">
                  LiteRent Application
                </span>
                <h1 className="text-4xl sm:text-5xl font-extrabold text-[#1a1a2e] leading-tight mb-4">
                  Apply for<br />
                  <span className="text-[#294c79]">{house.title}</span>
                </h1>
                <p className="text-[#1a1a1a]/60 text-lg mb-8">
                  We'll walk you through it one step at a time. Takes about 3 minutes.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="rounded-3xl bg-[#294c79] text-white p-6 shadow-lg">
                  <div className="text-xs uppercase tracking-widest text-white/60 mb-2">
                    Annual Rent
                  </div>
                  <div className="text-3xl font-extrabold">{formatNaira(plan.annualRent)}</div>
                  <div className="text-white/60 text-sm mt-1">{house.location}</div>
                </div>
                <div className="space-y-3">
                  <div className="rounded-2xl bg-[#f8fbf1] border border-[#e8f2d1] p-4">
                    <div className="text-xs text-[#4a7a1e] uppercase tracking-wide mb-1">
                      30% Upfront
                    </div>
                    <div className="text-xl font-bold text-[#294c79]">
                      {formatNaira(Math.ceil(plan.upfront))}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-[#f8fbf1] border border-[#e8f2d1] p-4">
                    <div className="text-xs text-[#4a7a1e] uppercase tracking-wide mb-1">
                      Monthly × 11
                    </div>
                    <div className="text-xl font-bold text-[#294c79]">
                      {formatNaira(Math.ceil(plan.monthly))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 mb-6">
                <strong>Verification fee:</strong> {formatNaira(plan.verificationFee)} — paid only
                after your application is reviewed. No deadline.
              </div>
              <button
                onClick={next}
                className="w-full py-4 rounded-2xl bg-[#a5c250] text-white font-bold text-lg hover:bg-[#8bb83a] active:scale-[0.98] transition-all shadow-md"
              >
                Start Application →
              </button>
            </Slide>

            {/* ── SLIDE 1: Occupation ── */}
            <Slide visible={slide === 1}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Employment · 1 of 4
              </p>
              <label className={labelCls}>What is your occupation?</label>
              <p className={hintCls}>Your current job title or what you do for work.</p>
              <input
                ref={inputRef}
                type="text"
                className={inputCls}
                placeholder="e.g. Software Engineer, Nurse, Business Owner"
                value={fd.occupation}
                onChange={e => set('occupation', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fd.occupation.trim() && next()}
              />
              <div className="flex gap-3">
                <button onClick={next} disabled={!fd.occupation.trim()} className={btnCls}>
                  OK, next →
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 2: Employer ── */}
            <Slide visible={slide === 2}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Employment · 2 of 4
              </p>
              <label className={labelCls}>Who do you work for?</label>
              <p className={hintCls}>
                Your employer's name or your business name if self-employed.
              </p>
              <input
                ref={inputRef}
                type="text"
                className={inputCls}
                placeholder="e.g. Andela, Federal Ministry of Health, My Business Ltd"
                value={fd.employer}
                onChange={e => set('employer', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fd.employer.trim() && next()}
              />
              <div className="flex gap-3">
                <button onClick={next} disabled={!fd.employer.trim()} className={btnCls}>
                  OK, next →
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 3: Monthly Income ── */}
            <Slide visible={slide === 3}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Employment · 3 of 4
              </p>
              <label className={labelCls}>What is your monthly take-home income?</label>
              <p className={hintCls}>
                After tax and deductions. Used to assess affordability — kept confidential.
              </p>
              <div className="relative">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-lg font-semibold text-[#294c79]/50">
                  ₦
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  className={inputCls + ' pl-10'}
                  placeholder="e.g. 350000"
                  value={fd.monthlyIncome}
                  onChange={e => set('monthlyIncome', e.target.value.replace(/\D/g, ''))}
                  onKeyDown={e => e.key === 'Enter' && fd.monthlyIncome && next()}
                />
              </div>
              {fd.monthlyIncome && (
                <p className="mt-2 text-sm text-[#4a7a1e] font-medium">
                  {formatNaira(fd.monthlyIncome)} / month
                </p>
              )}
              <div className="flex gap-3">
                <button onClick={next} disabled={!fd.monthlyIncome} className={btnCls}>
                  OK, next →
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 4: Years Employed ── */}
            <Slide visible={slide === 4}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Employment · 4 of 4
              </p>
              <label className={labelCls}>How long have you been in this job or business?</label>
              <p className={hintCls}>In years. Enter 0 if less than a year.</p>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                className={inputCls}
                placeholder="e.g. 3"
                value={fd.yearsEmployed}
                onChange={e => set('yearsEmployed', e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && next()}
              />
              <div className="flex gap-3">
                <button onClick={next} className={btnCls}>OK, next →</button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 5: Bank ── */}
            <Slide visible={slide === 5}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Finance · 1 of 4
              </p>
              <label className={labelCls}>Which bank do you use?</label>
              <p className={hintCls}>
                Only banks that support Paystack Direct Debit are listed — these are the banks
                your monthly rent will be automatically debited from.
              </p>

              {banksLoading ? (
                <div className="flex items-center gap-3 py-4 text-[#294c79]/60 text-sm">
                  <div className="animate-spin h-5 w-5 border-2 border-[#294c79] border-t-transparent rounded-full" />
                  Loading supported banks…
                </div>
              ) : (
                <select
                  ref={inputRef}
                  className={inputCls + ' cursor-pointer'}
                  value={fd.bankName}
                  onChange={e => setBank(e.target.value)}
                >
                  <option value="">Select your bank…</option>
                  {banks.map(b => (
                    <option key={b.code} value={b.name}>
                      {b.name}
                    </option>
                  ))}
                </select>
              )}

              {/* Notice about bank not being listed */}
              <p className="mt-3 text-xs text-[#1a1a1a]/40">
                Don't see your bank?{' '}
                <a
                  href="https://wa.me/2349057582412"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#294c79] underline"
                >
                  Contact us on WhatsApp
                </a>{' '}
                — we'll advise on the best option for you.
              </p>

              <div className="flex gap-3">
                <button onClick={next} disabled={!fd.bankName} className={btnCls}>
                  OK, next →
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 6: Account Number ── */}
            <Slide visible={slide === 6}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Finance · 2 of 4
              </p>
              <label className={labelCls}>What's your 10-digit account number?</label>
              <p className={hintCls}>
                For {fd.bankName || 'your bank'}. We'll auto-verify the account name.
              </p>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                maxLength={10}
                className={inputCls}
                placeholder="0123456789"
                value={fd.accountNumber}
                onChange={e => setAcct(e.target.value)}
              />
              {fd.accountNumber.length === 10 && (
                <div
                  className={`mt-3 p-4 rounded-2xl text-sm font-semibold ${
                    resolvingAccount
                      ? 'bg-[#f3f7fb] text-[#294c79]'
                      : fd.accountName
                      ? 'bg-[#f8fbf1] border border-[#c8dfa0] text-[#2d6a39]'
                      : accountNameError
                      ? 'bg-red-50 border border-red-200 text-red-700'
                      : ''
                  }`}
                >
                  {resolvingAccount
                    ? '⏳ Verifying account…'
                    : fd.accountName
                    ? `✅ ${fd.accountName}`
                    : accountNameError || ''}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={next}
                  disabled={fd.accountNumber.length !== 10 || !fd.accountName || resolvingAccount}
                  className={btnCls}
                >
                  OK, next →
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 7: BVN ── */}
            <Slide visible={slide === 7}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Finance · 3 of 4
              </p>
              <label className={labelCls}>What is your BVN?</label>
              <p className={hintCls}>
                Your 11-digit Bank Verification Number. Dial <strong>*565*0#</strong> on any
                Nigerian network to retrieve it. Stored securely and encrypted.
              </p>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                maxLength={11}
                className={inputCls}
                placeholder="12345678901"
                value={fd.bvn}
                onChange={e => set('bvn', e.target.value.replace(/\D/g, '').slice(0, 11))}
                onKeyDown={e => e.key === 'Enter' && fd.bvn.length === 11 && next()}
              />
              {fd.bvn.length > 0 && (
                <p
                  className={`mt-2 text-sm font-medium ${
                    fd.bvn.length === 11 ? 'text-[#4a7a1e]' : 'text-[#1a1a1a]/40'
                  }`}
                >
                  {fd.bvn.length}/11 digits
                </p>
              )}
              <div className="flex gap-3">
                <button onClick={next} disabled={fd.bvn.length !== 11} className={btnCls}>
                  OK, next →
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 8: Finance Review ── */}
            <Slide visible={slide === 8}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Finance · 4 of 4
              </p>
              <label className={labelCls}>Financial details confirmed ✓</label>
              <p className={hintCls}>Here's a summary of what you entered.</p>
              <div className="space-y-3 mb-4">
                {[
                  ['Bank', fd.bankName],
                  ['Account Name', fd.accountName],
                  ['Account Number', `••••••${fd.accountNumber.slice(-4)}`],
                  ['BVN', `••••••${fd.bvn.slice(-4)}`],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex justify-between items-center p-4 rounded-2xl bg-[#fbfcfe] border border-[#edf2f7] text-sm"
                  >
                    <span className="text-[#1a1a1a]/50">{k}</span>
                    <span className="font-semibold text-[#294c79]">{v}</span>
                  </div>
                ))}
              </div>
              {/* Direct debit notice */}
              <div className="rounded-2xl border border-[#c0d4f5] bg-[#f0f5ff] p-4 text-sm text-[#294c79] mb-2">
                🏦 <strong>This account will be used for direct debit.</strong> Once your
                application is approved and you authorise it, LiteRent will automatically debit{' '}
                <strong>{formatNaira(Math.ceil(plan.monthly))}</strong> monthly from{' '}
                <strong>{fd.accountName}</strong> at <strong>{fd.bankName}</strong>.
              </div>
              <div className="flex gap-3">
                <button onClick={next} className={btnCls}>Looks good →</button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 9: Emergency Contact ── */}
            <Slide visible={slide === 9}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Emergency Contact
              </p>
              <label className={labelCls}>Who should we contact in an emergency?</label>
              <p className={hintCls}>Someone who knows you well — a family member or close friend.</p>
              <div className="space-y-4">
                <input
                  ref={inputRef}
                  type="text"
                  className={inputCls}
                  placeholder="Full name"
                  value={fd.emergencyContactName}
                  onChange={e => set('emergencyContactName', e.target.value)}
                />
                <input
                  type="tel"
                  className={inputCls}
                  placeholder="Phone number (e.g. 08012345678)"
                  value={fd.emergencyContactPhone}
                  onChange={e => set('emergencyContactPhone', e.target.value)}
                />
                <input
                  type="text"
                  className={inputCls}
                  placeholder="Relationship (e.g. Sister, Father)"
                  value={fd.emergencyContactRelationship}
                  onChange={e => set('emergencyContactRelationship', e.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={next}
                  disabled={!fd.emergencyContactName.trim() || !fd.emergencyContactPhone.trim()}
                  className={btnCls}
                >
                  OK, next →
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 10: Guarantor 1 - Name & Contact ── */}
            <Slide visible={slide === 10}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Guarantor 1 · 1 of 2
              </p>
              <label className={labelCls}>Who is your first guarantor?</label>
              <p className={hintCls}>
                Must be someone with a steady income and a known address. They'll co-sign a
                guarantor form.
              </p>
              <div className="space-y-4">
                <input
                  ref={inputRef}
                  type="text"
                  className={inputCls}
                  placeholder="Full name"
                  value={fd.guarantor1Name}
                  onChange={e => set('guarantor1Name', e.target.value)}
                />
                <input
                  type="tel"
                  className={inputCls}
                  placeholder="Phone number"
                  value={fd.guarantor1Phone}
                  onChange={e => set('guarantor1Phone', e.target.value)}
                />
                <input
                  type="text"
                  className={inputCls}
                  placeholder="Relationship to you (e.g. Uncle, Colleague)"
                  value={fd.guarantor1Relationship}
                  onChange={e => set('guarantor1Relationship', e.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={next}
                  disabled={!fd.guarantor1Name.trim() || !fd.guarantor1Phone.trim()}
                  className={btnCls}
                >
                  OK, next →
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 11: Guarantor 1 - Work & Address ── */}
            <Slide visible={slide === 11}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Guarantor 1 · 2 of 2
              </p>
              <label className={labelCls}>
                A bit more about {fd.guarantor1Name || 'your guarantor'}.
              </label>
              <p className={hintCls}>Their work details and address help us assess their standing.</p>
              <div className="space-y-4">
                <input
                  ref={inputRef}
                  type="text"
                  className={inputCls}
                  placeholder="Occupation"
                  value={fd.guarantor1Occupation}
                  onChange={e => set('guarantor1Occupation', e.target.value)}
                />
                <input
                  type="text"
                  className={inputCls}
                  placeholder="Employer / Business name"
                  value={fd.guarantor1Employer}
                  onChange={e => set('guarantor1Employer', e.target.value)}
                />
                <input
                  type="text"
                  className={inputCls}
                  placeholder="Home address"
                  value={fd.guarantor1Address}
                  onChange={e => set('guarantor1Address', e.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={next}
                  disabled={!fd.guarantor1Address.trim()}
                  className={btnCls}
                >
                  OK, next →
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 12: Guarantor 2 (Optional) ── */}
            <Slide visible={slide === 12}>
              <p className="text-xs uppercase tracking-widest text-[#294c79]/50 mb-3">
                Guarantor 2 — Optional
              </p>
              <label className={labelCls}>
                Add a second guarantor?{' '}
                <span className="text-[#294c79]/40 font-normal text-lg">(Recommended)</span>
              </label>
              <p className={hintCls}>
                A second guarantor strengthens your application. You can skip this if needed.
              </p>
              <div className="space-y-4">
                <input
                  ref={inputRef}
                  type="text"
                  className={inputCls}
                  placeholder="Full name"
                  value={fd.guarantor2Name}
                  onChange={e => set('guarantor2Name', e.target.value)}
                />
                <input
                  type="tel"
                  className={inputCls}
                  placeholder="Phone number"
                  value={fd.guarantor2Phone}
                  onChange={e => set('guarantor2Phone', e.target.value)}
                />
                <input
                  type="text"
                  className={inputCls}
                  placeholder="Relationship"
                  value={fd.guarantor2Relationship}
                  onChange={e => set('guarantor2Relationship', e.target.value)}
                />
                <input
                  type="text"
                  className={inputCls}
                  placeholder="Occupation (optional)"
                  value={fd.guarantor2Occupation}
                  onChange={e => set('guarantor2Occupation', e.target.value)}
                />
                <input
                  type="text"
                  className={inputCls}
                  placeholder="Address (optional)"
                  value={fd.guarantor2Address}
                  onChange={e => set('guarantor2Address', e.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <button onClick={next} className={btnCls}>
                  {fd.guarantor2Name.trim() ? 'OK, next →' : 'Skip & continue →'}
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 13: Review ── */}
            <Slide visible={slide === 13}>
              <label className={labelCls}>Almost done — review your application.</label>
              <p className={hintCls}>Make sure everything looks right before you submit.</p>
              <div className="space-y-3 mb-6 text-sm">
                {[
                  ['Occupation', fd.occupation],
                  ['Employer', fd.employer],
                  ['Monthly income', fd.monthlyIncome ? formatNaira(fd.monthlyIncome) : '—'],
                  ['Bank', fd.bankName],
                  ['Account name', fd.accountName],
                  ['Emergency contact', fd.emergencyContactName],
                  ['Guarantor 1', fd.guarantor1Name],
                  fd.guarantor2Name ? ['Guarantor 2', fd.guarantor2Name] : null,
                ]
                  .filter(Boolean)
                  .map(([k, v]) => (
                    <div
                      key={k}
                      className="flex justify-between items-center p-3.5 rounded-2xl bg-[#fbfcfe] border border-[#edf2f7]"
                    >
                      <span className="text-[#1a1a1a]/50">{k}</span>
                      <span className="font-semibold text-[#294c79]">{v}</span>
                    </div>
                  ))}
              </div>
              <div className="p-4 rounded-2xl border border-[#d9e5ef] bg-[#f8fbff] text-xs text-[#1a1a1a]/60 mb-2">
                By submitting, you confirm all details are accurate and authorise LiteRent to
                verify your profile, documents, and BVN as part of its process.
              </div>
              <label className="flex items-center gap-3 p-4 rounded-2xl border border-[#d9e5ef] bg-white cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={fd.termsAccepted}
                  onChange={e => set('termsAccepted', e.target.checked)}
                  className="w-5 h-5 rounded text-[#294c79]"
                />
                <span className="text-sm text-[#1a1a1a]/70">
                  I confirm this information is accurate and I accept LiteRent's{' '}
                  <Link to="/terms" className="text-[#294c79] underline">
                    Terms
                  </Link>
                  .
                </span>
              </label>
              {submitError && (
                <div className="my-3 p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  {submitError}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={submitApplication}
                  disabled={submitting || !fd.termsAccepted}
                  className={btnCls}
                >
                  {submitting ? 'Submitting…' : 'Submit Application ✓'}
                </button>
                <button onClick={back} className={backBtnCls}>← Back</button>
              </div>
            </Slide>

            {/* ── SLIDE 14: Success ── */}
            <Slide visible={slide === 14}>
              <div className="text-center py-8">
                <div className="w-24 h-24 rounded-full bg-[#e8f2d1] flex items-center justify-center mx-auto mb-6 text-5xl">
                  🎉
                </div>
                <h1 className="text-3xl font-extrabold text-[#294c79] mb-3">
                  Application Submitted!
                </h1>
                <p className="text-[#1a1a1a]/60 text-lg mb-8">
                  You're all set. Track each stage — application, verification, upfront payment,
                  and your active rental — from your dashboard.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full py-4 rounded-2xl bg-[#294c79] text-white font-bold text-lg hover:bg-[#1f3a5c] transition"
                  >
                    Go to My Dashboard →
                  </button>
                  <Link
                    to="/browse"
                    className="block w-full py-3 rounded-2xl border border-[#294c79] text-[#294c79] font-semibold text-center hover:bg-[#f3f7fb] transition"
                  >
                    Browse More Homes
                  </Link>
                </div>
              </div>
            </Slide>
          </div>
        </main>

        <footer className="py-5 text-center text-[#1a1a1a]/40 text-xs border-t border-[#edf2f7]">
          © {new Date().getFullYear()} LiteRent · All rights reserved
        </footer>
      </div>
    </>
  );
}
